import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "../../Components/Common/TableContainer";
import {
  getIndividuals,
  deleteIndividual,
} from "common/services/individuals.service";
import AddIndividual from "./AddIndividual";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import { isPaymentActive } from "common/lib/paymentOptions";
import UpdateIndividual from "./UpdateIndividual";
import Permission from "Components/Common/Permission";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";
import Permissions from "Components/Common/Permissions";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import Import from "./Import";
import * as moment from "moment";
import BulkActionBar from "Components/Common/BulkActionBar";

const defaultSort = [{ id: "createdAt", desc: true }];

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  paymentType: string;
  agency: string;
  dateRange: DateRange;
  gender: string;
}

const defaultFilterValue: FilterValue = {
  paymentType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
  gender: "",
};

// @ts-ignore
// const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
//   if (!value) return null;
//   const matches = value.match(/.{1,40}/g);
//   const wrappedText = matches ? matches.join("\n") : ""; // Handle null case
//   return (
//     <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{wrappedText}</div>
//   );
// };

const countActiveContracts = (payments: any) => {
  let activeContractsCount = 0;

  if (payments && Array.isArray(payments)) {
    payments.forEach((payment) => {
      const { startDate, endDate } = payment;

      if (isPaymentActive(startDate, endDate)) {
        activeContractsCount += 1;
      }
    });
  }

  return activeContractsCount;
};

const Individuals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams), // Starts from 1
    pageSize: parsePageSize(searchParams), // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() =>
    parseSort(searchParams.get("sort"), defaultSort)
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const fetchContracts = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  const [filterValue, setFilterValue] = useState(() =>
    parseFilter(searchParams, defaultFilterValue)
  );
  const unCheckAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    if (checkall) checkall.checked = false;
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency,
            },
          },
        });
      }
      if (filterValue?.paymentType) {
        andConditions.push({
          paymentType: {
            id: {
              $eq: filterValue.paymentType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
            {
              endDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
          ],
        });
      }
      if (filterValue?.gender) {
        andConditions.push({
          gender: {
            $eq: filterValue.gender,
          },
        });
      }

      andConditions.push({
        $or: [
          {
            fullName: {
              $containsi: searchValue,
            },
          },
          {
            emailAddress: {
              $containsi: searchValue,
            },
          },
          {
            passportOrIdNumber: {
              $containsi: searchValue,
            },
          },
          {
            phoneNumber: {
              $containsi: searchValue,
            },
          },
        ],
      });

      const APIParams = {
        pagination: {
          page,
          pageSize: pagination.pageSize,
        },
        sort: sortObj,

        populate: {
          payments: true,
        },
        filters: {
          $and: andConditions,
        },
      };
      setApiParams(APIParams);

      try {
        setLoading(true);
        unCheckAll();
        const response: any = await getIndividuals(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteIndividual(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Individual deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting payment");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteIndividual(id);
          })
        );
        setDeleting(false);
        toast.success("Individuals deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "individual",
        action: "delete",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "individual",
        action: "update",
      },
    ]),
    createdAt: isAllowed(userProfile?.permissions, [
      {
        resource: "individual",
        action: "create",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Recipient Name",
        accessorKey: "fullName",
        id: "fullName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "300px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded-circle fs-24">
                      <i className="ri-user-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link
                          to={`/individuals/${rowData.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/individuals/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: "Gender",
        accessorKey: "gender",
        enableColumnFilter: false,
        id: "gender",
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },
      {
        header: "Passport or ID",
        accessorKey: "passportOrIdNumber",
        enableColumnFilter: false,
        id: "passportOrIdNumber",
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },
      {
        header: "Email",
        accessorKey: "emailAddress",
        enableColumnFilter: false,
        id: "emailAddress",
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },
      {
        header: "Number of contracts",
        accessorKey: "contracts",
        enableColumnFilter: false,
        enableSorting: false,
        id: "contracts",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const noOfContracts = rowData?.payments?.length || 0;
          const label = noOfContracts === 1 ? "Contract" : "Contracts";
          return (
            <>
              <h5 className="fs-14 my-1 fw-normal">{noOfContracts}</h5>
              <span className="text-muted">{label}</span>
            </>
          );
        },
      },

      {
        header: "Current Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const countOfActiveContracts = countActiveContracts(
            cell.row.original.payments
          );

          return (
            <React.Fragment>
              {countOfActiveContracts === 0 ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  No Active Contracts
                </span>
              ) : countOfActiveContracts === 1 ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  {countOfActiveContracts} Active Contract
                </span>
              ) : countOfActiveContracts > 1 ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  {countOfActiveContracts} Active Contracts
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  No Active Contracts
                </span>
              )}
            </React.Fragment>
          );
        },
      },

      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        id: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Created At",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="individual" action="findOne">
                  <DropdownItem
                    href={`/individuals/${rowData.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/individuals/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="individual" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="individual" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Individual Recipients"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Card>
            <CardBody>
              <Row className="g-3 ">
                <Col xxl={7} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">Search</h6> */}
                  <div className="search-box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search individual recipients..."
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        setPage(1);
                      }}
                      value={searchValue}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <Col xxl={4} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">Select Gender</h6> */}
                  <select
                    className="form-control"
                    data-choices
                    data-choices-search-false
                    name="choices-single-default2"
                    id="choices-single-default2"
                    onChange={(e: any) => {
                      setFilterValue({
                        ...filterValue,
                        gender: e.target.value,
                      });
                      setPage(1);
                    }}
                    value={filterValue.gender}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </Col>

                <Col xxl={1} lg={4}>
                  {/* <h6 className="text-uppercase fs-12 mb-xxl-4"> {` `}</h6> */}
                  {/* <button
                    onClick={() => {
                      setFilterValue(defaultFilterValue);
                      setSearchValue("");
                      setPage(1);
                      setSorted([{ id: "createdAt", desc: true }]);
                    }}
                    className="btn btn-outline-primary material-shadow-none w-100 align-bottom"
                  >
                    <i className="ri-equalizer-line align-bottom me-1"></i>{" "}
                    Reset Filters
                  </button> */}

                  <div className="w-100 d-flex gap-2">
                    <button
                      className="flex-grow-1 btn btn-light"
                      onClick={() => {
                        setFilterValue(defaultFilterValue);
                        setSearchValue("");
                        setPage(1);
                        setSorted([{ id: "createdAt", desc: true }]);
                      }}
                    >
                      <i className="ri-restart-line align-bottom me-1"></i>
                      Reset{" "}
                    </button>

                    {/* <Button
                                  color="primary"
                                  className="flex-grow-1"
                                  onClick={() => {
                                    setAdvancedFilter(true);
                                  }}
                                >
                                  <i className="ri-equalizer-line align-bottom me-1"></i>
                                  Filters
                                </Button> */}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">Individual Recipients</h5>
                  <p className="mb-0 text-muted">
                    A list of individuals/recipients of salary disbursements.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission resource="individual" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          showAddModalForm();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Individual
                      </Link>
                    </Permission>
                    <Permissions
                      checks={[
                        {
                          resource: "individual",
                          action: "import",
                        },
                        {
                          resource: "individual",
                          action: "exportToCsv",
                        },
                        {
                          resource: "individual",
                          action: "clearAll",
                        },
                      ]}
                    >
                      <Import apiParams={apiParams} fetchData={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddIndividual
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchContracts={fetchContracts}
              />
              <UpdateIndividual
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteModal}
                onDeleteClick={() => handleDelete()}
                onCloseClick={() => setDeleteModal(false)}
                loading={deleting}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
            </CardBody>
          </Card>
        </Container>
        <Permissions
          checks={[{ resource: "individual", action: "delete" }]}
        >
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="individual"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default Individuals;
